// external
import type { Store } from 'redux';

// libraries
import { BaseMiddleware, logNewRelicPageAction } from '@makemydeal/dr-activities-common';

// types
import type { IVehicle } from '@makemydeal/dr-platform-types';
import type { AppliedFilter, ConfigChangeVehicle, HostActions } from '../types';

// store
import {
    fetchVehicleAgentResults,
    newVehicleSelected,
    processCviVehicles,
    processVehicleAgentRequestWithFilters,
    setAgentPromptAttr,
    verifyAiSearchVinInventory
} from './actionCreators';
import { isSameVin, isVinLike } from '../utils/aiInventoryFilter';
import * as Actions from './actions';
import type { Selectors } from './selectors';
import { catalogToVehicle, formatFilterObject, getPromptKind } from '../utils';
import { AI_PROMPT_KIND, AI_PROMPT_MODE, AI_PROMPT_PLACEHOLDER, AI_REQUEST_TRIGGER } from '../constants';

type AgentRequestTiming = { source: 'search' | 'filterSummary'; phase: 'REQUEST' | 'SUCCESS' | 'FAILURE' };
type AgentRequestContext = { trigger?: AI_REQUEST_TRIGGER; promptKind?: AI_PROMPT_KIND };
type AgentRequestStart = AgentRequestContext & { startedAt: number };
type InventoryFallbackStart = { startedAt: number; relaxedFilters?: string[] };

const AGENT_REQUEST_TIMING: Record<string, AgentRequestTiming> = {
    [Actions.VEHICLE_AGENT_REQUEST]: { source: 'search', phase: 'REQUEST' },
    [Actions.VEHICLE_AGENT_SUCCESS]: { source: 'search', phase: 'SUCCESS' },
    [Actions.VEHICLE_AGENT_FAILURE]: { source: 'search', phase: 'FAILURE' },
    [Actions.VEHICLE_AGENT_FILTER_REQUEST]: { source: 'filterSummary', phase: 'REQUEST' },
    [Actions.VEHICLE_AGENT_FILTER_SUCCESS]: { source: 'filterSummary', phase: 'SUCCESS' },
    [Actions.VEHICLE_AGENT_FILTER_FAILURE]: { source: 'filterSummary', phase: 'FAILURE' }
};

export class MiddlewareManager extends BaseMiddleware {
    constructor(
        public selectors: Selectors,
        public hostActions: HostActions,
        public config: ConfigChangeVehicle,
        public baseRoute?: string
    ) {
        super(selectors);
    }

    private agentRequestStarts: Record<string, AgentRequestStart> = {};

    private pendingAgentRequestContext: AgentRequestContext = {};

    private inventoryFallbackStart?: InventoryFallbackStart;

    private getInventoryFallbackLogFields = (store: Store<any>, action: any) => ({
        coxAutoCorrelationId: action.meta.coxAutoCorrelationId,
        commonOrgId: this.selectors.additionalSelectors.getCommonOrgId(store.getState()),
        dealXgId: this.selectors.additionalSelectors.getDealId?.(store.getState())
    });

    private handleInventoryFallback = (store: Store<any>, action: any): void => {
        const isSuccess = action.type === Actions.VEHICLE_AGENT_SUCCESS;

        if (action.meta?.trigger === AI_REQUEST_TRIGGER.INVENTORY_FALLBACK) {
            const start = this.inventoryFallbackStart;
            if (start === undefined) return;
            this.inventoryFallbackStart = undefined;

            if (action.meta.isAborted) {
                logNewRelicPageAction('AI vehicle search inventory fallback', {
                    phase: 'aborted',
                    trigger: AI_REQUEST_TRIGGER.INVENTORY_FALLBACK,
                    durationMs: Math.round(performance.now() - start.startedAt),
                    relaxedFilters: start.relaxedFilters,
                    ...this.getInventoryFallbackLogFields(store, action)
                });
                return;
            }

            logNewRelicPageAction('AI vehicle search inventory fallback', {
                phase: 'completed',
                trigger: AI_REQUEST_TRIGGER.INVENTORY_FALLBACK,
                success: isSuccess,
                resultCount: action.payload?.count,
                durationMs: Math.round(performance.now() - start.startedAt),
                relaxedFilters: start.relaxedFilters,
                ...this.getInventoryFallbackLogFields(store, action)
            });
            return;
        }

        const isFallbackTriggeringResponse =
            isSuccess &&
            action.meta?.promptMode === AI_PROMPT_MODE.SIMILARITY_SEARCH &&
            action.meta.trigger === AI_REQUEST_TRIGGER.DRAWER_OPEN &&
            this.selectors.getIsAgentFallbackPending(store.getState());
        if (!isFallbackTriggeringResponse) return;

        const relaxedFilters = action.payload.relaxedFilters;
        this.inventoryFallbackStart = { startedAt: performance.now(), relaxedFilters };

        logNewRelicPageAction('AI vehicle search inventory fallback', {
            phase: 'dispatched',
            trigger: AI_REQUEST_TRIGGER.INVENTORY_FALLBACK,
            relaxedFilters,
            ...this.getInventoryFallbackLogFields(store, action)
        });

        store.dispatch(setAgentPromptAttr(AI_PROMPT_PLACEHOLDER.ALL_VEHICLES, AI_PROMPT_MODE.SEMANTIC_SEARCH));
        store.dispatch(processVehicleAgentRequestWithFilters({ trigger: AI_REQUEST_TRIGGER.INVENTORY_FALLBACK }));
    };

    private trackAgentRequestTiming = (store: Store<any>, action: any, { source, phase }: AgentRequestTiming): void => {
        if (phase === 'REQUEST') {
            const context: AgentRequestContext =
                source === 'filterSummary'
                    ? { trigger: AI_REQUEST_TRIGGER.DRAWER_OPEN, promptKind: AI_PROMPT_KIND.ALL_VEHICLES }
                    : this.pendingAgentRequestContext;
            this.agentRequestStarts[source] = { ...context, startedAt: performance.now() };
            return;
        }

        // An aborted request's FAILURE can land after its replacement's REQUEST, so reporting it would consume the replacement's start time.
        if (action.meta?.isAborted) return;

        const start = this.agentRequestStarts[source];
        if (start === undefined) return;
        delete this.agentRequestStarts[source];

        logNewRelicPageAction('AI vehicle search request completed', {
            source,
            success: phase === 'SUCCESS',
            durationMs: Math.round(performance.now() - start.startedAt),
            trigger: start.trigger,
            promptKind: start.promptKind,
            promptMode: action.meta?.promptMode,
            status: action.payload?.status,
            resultCount: action.payload?.count,
            coxAutoCorrelationId: action.meta?.coxAutoCorrelationId,
            commonOrgId: this.selectors.additionalSelectors.getCommonOrgId(store.getState()),
            dealXgId: this.selectors.additionalSelectors.getDealId?.(store.getState()),
            promptOptimizationEnabled: this.selectors.additionalSelectors.isPromptOptimizationEnabled?.(store.getState()) ?? false
        });
    };

    /**
     * any activity specific middleware code should be defined here;
     * application-specific middleware should be outside of the activity
     */
    protected middlewareFunction = (store: Store<any>, next: any, action: any): void => {
        const agentRequestTiming = AGENT_REQUEST_TIMING[action.type];
        if (agentRequestTiming) {
            this.trackAgentRequestTiming(store, action, agentRequestTiming);
        }

        if (action.type === Actions.VEHICLE_AGENT_SUCCESS || action.type === Actions.VEHICLE_AGENT_FAILURE) {
            this.handleInventoryFallback(store, action);
        }

        switch (action.type) {
            case Actions.SEARCH_VEHICLE_SUCCESS:
            case Actions.FETCH_CVI_SUCCESS:
            case Actions.VEHICLE_AGENT_SUCCESS: {
                const vehicles = action.payload.vehicles;
                if (vehicles) {
                    store.dispatch(processCviVehicles(vehicles.map(catalogToVehicle)));
                }
                break;
            }

            /**
             * Reported here rather than from the action's `meta`: a callback there would replace the vin
             * the reducer reads, and it receives the raw response, not the parsed vehicles. `next` has
             * already run by this point (`BaseMiddleware.middleware`), so this reports the verdict the
             * reducer kept rather than re-deriving it — an answer for a prompt the user has since
             * replaced is discarded there, and counting it would overstate how often the index is stale.
             */
            case Actions.AI_SEARCH_VIN_INVENTORY_SUCCESS: {
                const vin = action.meta?.vin;

                logNewRelicPageAction('AI search VIN inventory check completed', {
                    success: true,
                    vin,
                    markedOutOfStock: isSameVin(this.selectors.getAiSearchInactiveVin(store.getState()), vin)
                });
                break;
            }

            case Actions.PROCESS_VEHICLE_AGENT_REQUEST: {
                const state = store.getState();

                // A new AI search starts fresh, so drop any carried-over selection — the classic search
                // path already does this via SEARCH_VEHICLE_REQUEST. Gated to the AVO feature so Change
                // Vehicle in other apps keeps its existing selection behavior.
                if (this.selectors.isAiAltVehicleOptionsActive(state)) {
                    store.dispatch(newVehicleSelected({} as IVehicle));
                }

                const { appliedFilters, sortBy, selectedPage, vehicle, trigger } = action.payload ?? {};

                const { getCommonOrgId, getDealId, getServicesBff } = this.selectors.additionalSelectors;
                const commonOrgDealerId = getCommonOrgId(state);
                const dealId = getDealId?.(state);
                const bff = getServicesBff(state);
                const applicationName = this.config.applicationName;
                const dealershipFilterIds =
                    appliedFilters
                        ?.filter((item: AppliedFilter) => item.id === 'dealerships' && item.value === true)
                        .map((item: AppliedFilter) => item.label) || [];

                const getCommonOrgDealerIds = (): string[] => {
                    // Use filter-selected dealerships if any are selected
                    if (dealershipFilterIds.length > 0) {
                        return dealershipFilterIds;
                    }

                    // Fall back to all available dealerships
                    const dealerships = this.selectors.getDealerships(state);
                    if (!dealerships?.length) {
                        return [];
                    }

                    return dealerships.map((d) => d.commonOrgId).filter((id): id is string => Boolean(id));
                };

                const commonOrgDealerIds = getCommonOrgDealerIds();

                const agentPromptString = this.selectors.getAgentPromptString(state);
                const vehicleVin = this.selectors.getVehicleVin(state);
                const vehicleStockNumber = this.selectors.getVehicleStockNumber(state);
                const agentFilterSummary = this.selectors.getAgentFilterSummary(state);
                const currentPromptMode = this.selectors.getAgentPromptMode(state);

                const hasUserEnteredPrompt =
                    currentPromptMode === AI_PROMPT_MODE.SEMANTIC_SEARCH &&
                    agentPromptString &&
                    agentPromptString.trim().length > 0;

                const promptMode =
                    currentPromptMode !== AI_PROMPT_MODE.DEFAULT ? currentPromptMode : AI_PROMPT_MODE.SIMILARITY_SEARCH;

                const promptString = hasUserEnteredPrompt ? agentPromptString : AI_PROMPT_PLACEHOLDER.SEARCH;

                const isPromptOptimizationEnabled =
                    this.selectors.additionalSelectors.isPromptOptimizationEnabled?.(state) ?? false;

                const isBodyStyleRelaxationEnabled =
                    this.selectors.additionalSelectors.isSimilarityBodyStyleRelaxationEnabled?.(state) ?? false;

                const filters = formatFilterObject(appliedFilters, agentFilterSummary);

                this.pendingAgentRequestContext = { trigger, promptKind: getPromptKind(promptString, promptMode) };

                store.dispatch(
                    fetchVehicleAgentResults(
                        promptString,
                        commonOrgDealerId,
                        bff,
                        applicationName || '',
                        commonOrgDealerIds,
                        vehicleStockNumber || '',
                        vehicleVin || '',
                        promptMode,
                        filters,
                        selectedPage,
                        vehicle,
                        sortBy,
                        dealId,
                        isPromptOptimizationEnabled,
                        trigger,
                        isBodyStyleRelaxationEnabled
                    )
                );

                // Verify a bare-VIN prompt against live inventory, alongside the search. `promptString`'s
                // only other value is the non-VIN `'search'` placeholder, so prompt-less modes are
                // excluded by shape.
                if (isVinLike(promptString)) {
                    // Every rooftop the search covered, so sibling-rooftop stock is not read as gone. The
                    // trade it accepts: a vehicle that moved between rooftops inside the index window
                    // answers "in stock" from its new home while the row still shows the old one's record.
                    const rooftops = commonOrgDealerIds.length ? commonOrgDealerIds : [commonOrgDealerId];
                    store.dispatch(verifyAiSearchVinInventory(promptString.trim(), rooftops, bff));
                }
                break;
            }

            default: {
                break;
            }
        }
    };
}
